import { useMemo, useEffect, useCallback } from "react";

/**
 * useBillingCalculation
 *
 * Computes per-room charges and the overall billing summary (service charge,
 * tax, parking, previous balance offset, etc.) and syncs the derived totals
 * back into the form's `values` via setValues.
 *
 * Balance sign convention:
 *   PrevBalance > 0  => customer has CREDIT available (can offset current due)
 *   PrevBalance < 0  => customer has an outstanding DUE from before
 *   PrevBalance = 0  => no previous balance
 */
const useBillingCalculation = ({ rooms, values, taxConfig, setValues }) => {
    const percentOf = useCallback(
        (percent, base) => (Number(percent || 0) * Number(base || 0)) / 100,
        []
    );

    const diffInDays = useCallback((from, to) => {
        if (!from || !to) return 1;
        const diff = Math.ceil((new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24));
        return diff > 0 ? diff : 1;
    }, []);

    // ---------------------------------------------------------------------
    // Per-room computed charges
    // ---------------------------------------------------------------------
    const computedRooms = useMemo(() => {
        return rooms.map((room) => {

            const facilityFrom = values.checkin_date;
            const ExtraFacilityFrom = room.extracheck_in_date || values.checkin_date;
            const facilityTo = values.checkout_date;
            const ExtraFacilityTo = room.extracheck_out_date || values.checkout_date;
            const facilityDays = diffInDays(facilityFrom, facilityTo);
            const extraFacilityDays = diffInDays(ExtraFacilityFrom, ExtraFacilityTo);

            const extra_bed_charge = Number(room.extra_bed || 0) * Number(room.extra_bed_charge || 0) * extraFacilityDays;
            const extra_person_charge = Number(room.extra_person || 0) * Number(room.extra_person_charge || 0) * extraFacilityDays;
            const extra_child_charge = Number(room.extra_child || 0) * Number(room.extra_child_charge || 0) * extraFacilityDays;
            const extra_room_total = extra_bed_charge + extra_person_charge + extra_child_charge;

            const room_rate = Number(room.room_rate || 0);
            const room_rate_total = room_rate * facilityDays;

            const complementary_price = Number(room.complementary_price || 0);
            const complementary_price_type = room.complementary_price_type || "";

            const multiplier =
                room.accommodation_type === "DORMITORY"
                    ? Number(room.no_of_bed || 0)
                    : 1;

            const complementary_total =
                complementary_price_type === "DAY_WISE"
                    ? complementary_price * facilityDays * multiplier
                    : complementary_price * multiplier;

            const bed_rate_total = Number(room.rate_per_bed || 0) * Number(room.no_of_bed || 0) * facilityDays + complementary_total;

            const subtotal = room_rate_total + extra_room_total + complementary_total;

            const room_total = (room_rate_total) + (extra_room_total + complementary_total);

            const final_total = room.accommodation_type === "DORMITORY" ? bed_rate_total : room_total;


            return {
                ...room,
                room_rate,
                room_rate_total,
                complementary_total,
                complementary_price_type,
                complementary_price,
                facilityDays,
                extra_bed_charge,
                extra_person_charge,
                extra_child_charge,
                extra_room_total,
                subtotal,
                room_total,
                bed_rate_total,
                final_total,
            };
        });
    }, [rooms, values.checkin_date, values.checkout_date, diffInDays]);

    const billingDeps = useMemo(
        () => ({
            roomsSum: computedRooms.reduce((sum, r) => sum + (Number(r.final_total) || 0), 0),
            extrasSum: computedRooms.reduce((sum, r) => sum + (Number(r.extra_room_total) || 0), 0),
        }),
        [computedRooms]
    );

    // ---------------------------------------------------------------------
    // Aggregate billing summary
    // ---------------------------------------------------------------------
    const billingRates = useMemo(() => {
        if (rooms.length === 0) {
            return {
                baseRent: 0,
                extras: 0,
                tax: 0,
                total_tax_rate: 0,
                serviceCharge: 0,
                service_charge: 0,
                total: 0,
                RoomBookingCharge: 0,
                AdvanceAmount: 0,
                PrevBalance: 0,
                RemainingAmount: 0,
                parking_price: 0,
                parking_price_total: 0,
                facilityDays: 1,
            };
        }

        // ---- STEP 1: Room + extras subtotal ----
        const baseRent = billingDeps.roomsSum;

        const service_charge = Number(taxConfig?.service_charge ?? 0);

        // Parking uses global check-in/out dates, since it's a property-level charge
        const facilityDays = diffInDays(values.checkin_date, values.checkout_date);


        const parking_price = Number(taxConfig?.parking_price ?? 0);
        const parking_price_total =
            parking_price * Number(values.total_vehicle || 0) * facilityDays;


        // ---- STEP 2: Service charge — on room+extras subtotal ----
        const serviceCharge = percentOf(service_charge, baseRent);

        // ---- STEP 3: Tax — on (room+extras+service+parking) ----
        const taxableAmount = baseRent + serviceCharge + parking_price_total;
        let allTaxes = taxConfig?.all_taxs ?? [];

        try {
            while (typeof allTaxes === "string") {
                allTaxes = JSON.parse(allTaxes);
            }

            if (!Array.isArray(allTaxes)) {
                allTaxes = [];
            }
        } catch (err) {
            allTaxes = [];
        }

        const taxBreakdown = allTaxes.map((item) => ({
            tax_id: item.tax_id,
            tax_name: item.tax_name,
            rate: Number(item.rate) || 0,
            amount: percentOf(Number(item.rate) || 0, taxableAmount),
        }));

        const tax = taxBreakdown.reduce(
            (sum, item) => sum + item.amount,
            0
        );

        const total_tax_rate = taxBreakdown.reduce(
            (sum, item) => sum + item.rate,
            0
        );

        // ---- STEP 4: Final total ----
        const total = Math.round(baseRent + serviceCharge + parking_price_total + tax);
        const RoomBookingCharge = baseRent;

        // Sum advance amounts from all payment rows
        const AdvanceAmount = (values.PaymentDetail || []).reduce(
            (sum, p) => sum + Number(p.advance_amount || 0),
            0
        );

        // ---- STEP 5: Previous balance ----
        const PrevBalance = Number(values.avalible_balance || 0);

        // Whether the user has opted to apply the previous balance to this bill.
        // Defaults to true (applied) unless explicitly unchecked.
        const applyBalance = values.is_apply_balance ?? true;
        const is_prev_apply_balance = values?.is_prev_apply_balance;
        let prev_used_balance = ""
        if (is_prev_apply_balance) {
            prev_used_balance = Number(values?.prev_used_balance) || 0
        }

        const currentDue = Number(total || 0) - Number(AdvanceAmount || 0);
        const dueAfterPrevUsed = currentDue - Number(prev_used_balance || 0);

        const availableCredit = applyBalance && PrevBalance > 0 ? PrevBalance : 0;
        const outstandingDue = applyBalance && PrevBalance < 0 ? Math.abs(PrevBalance) : 0;

        const creditApplied = Math.min(availableCredit, Math.max(dueAfterPrevUsed, 0));

        const RemainingAmount = (dueAfterPrevUsed - creditApplied) + outstandingDue;

        const is_apply_balance_amount = applyBalance ? creditApplied : 0;


        return {
            baseRent,
            extras: billingDeps.extrasSum,

            tax,
            taxBreakdown,

            total_tax_rate,

            serviceCharge,
            service_charge,

            total,
            RoomBookingCharge,

            AdvanceAmount,
            PrevBalance,
            RemainingAmount,

            parking_price,
            parking_price_total,
            facilityDays,

            is_apply_balance_amount,
            is_prev_apply_balance,
            prev_used_balance
        };
    }, [
        billingDeps,
        taxConfig,
        percentOf,
        diffInDays,
        rooms.length,
        values.PaymentDetail,
        values.total_vehicle,
        values.checkin_date,
        values.checkout_date,
        values.avalible_balance,
        values.is_apply_balance
    ]);

    // ---------------------------------------------------------------------
    // Sync computed total, service charge, tax, parking rate, and used
    // balance back into form values. All writes go through setValues —
    // no direct mutation of `values` during render.
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (!rooms.length) return;

        setValues((prev) => {
            const next = { ...prev };
            let changed = false;

            const newTotal = String(billingRates.total);
            if (prev.total_amount !== newTotal) {
                next.total_amount = newTotal;
                changed = true;
            }

            if (prev.service_charge !== billingRates.service_charge) {
                next.service_charge = billingRates.service_charge;
                changed = true;
            }

            if (JSON.stringify(prev.tax_details) !== JSON.stringify(billingRates.taxBreakdown)) {
                next.tax_details = billingRates.taxBreakdown;
                changed = true;
            }

            if (prev.parking_rate !== billingRates.parking_price) {
                next.parking_rate = billingRates.parking_price;
                changed = true;
            }

            if (prev.advance_amount !== billingRates.AdvanceAmount) {
                next.advance_amount = billingRates.AdvanceAmount;
                changed = true;
            }

            if (prev.is_apply_balance_amount !== billingRates.is_apply_balance_amount) {
                next.is_apply_balance_amount = billingRates.is_apply_balance_amount;
                changed = true;
            }






            // ---------------------------------------------------------------
            // NEW: sync each room's computed final_total back into
            // values.rooms[i].room_total. Without this, formValues.rooms at
            // submit time still has room_total = "" (its initial value),
            // because computedRooms is a derived/local array that was never
            // written back to form state.
            // Matched by index — assumes prev.rooms and computedRooms stay
            // in the same order/length, which holds since computedRooms is
            // built by mapping over `rooms` (== prev.rooms) directly above.
            // ---------------------------------------------------------------
            const nextRooms = prev.rooms.map((room, idx) => {
                const computed = computedRooms[idx];
                if (!computed) return room;

                const newRoomTotal = computed.final_total;
                if (room.room_total !== newRoomTotal) {
                    changed = true;
                    return { ...room, room_total: newRoomTotal };
                }
                return room;
            });

            if (changed) {
                next.rooms = nextRooms;
            }

            return changed ? next : prev;
        });
    }, [billingRates, rooms.length, setValues, computedRooms]);

    return { billingRates, computedRooms };
};

export default useBillingCalculation;